/**
 * Textbook swap. Reachable only when TEXTBOOK_SWAP_ENABLED is true — the route
 * is not registered otherwise, and the API endpoints do not exist either.
 */
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiRequestError } from '../lib/api';
import { Callout, EmptyState, ErrorNotice, Spinner } from '../components/ui';

interface Listing {
  id: string;
  title: string;
  author: string | null;
  isbn: string | null;
  edition: string | null;
  condition: string;
  priceCents: number;
  currency: string;
  description: string | null;
  location: string | null;
  contactEmail: string | null;
  status?: string;
  listingFeePaid?: boolean;
  createdAt: string;
}

const CONDITIONS = [
  { value: 'new', label: 'New' },
  { value: 'like_new', label: 'Like new' },
  { value: 'good', label: 'Good' },
  { value: 'fair', label: 'Fair' },
  { value: 'poor', label: 'Poor' },
];

export function Marketplace() {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['listings'],
    queryFn: () => api.get<{ listings: Listing[] }>('/api/marketplace/listings'),
  });
  const { data: mine } = useQuery({
    queryKey: ['listings-mine'],
    queryFn: () => api.get<{ listings: Listing[] }>('/api/marketplace/listings/mine'),
  });

  const pay = async (id: string) => {
    setError(null);
    try {
      const { url } = await api.post<{ url: string | null }>(`/api/marketplace/listings/${id}/pay`);
      if (url) window.location.href = url;
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Could not start the listing payment.');
    }
  };

  if (isLoading) return <Spinner />;

  return (
    <div className="space-y-8">
      <header>
        <h1 className="text-3xl font-bold tracking-tight text-ink">Textbook swap</h1>
        <p className="mt-2 max-w-2xl text-muted">
          Buy and sell used AMH, APF and BNF copies with other candidates.
        </p>
      </header>

      <Callout tone="warn">
        Titrate charges a flat listing fee and nothing else. We are not a party to the sale: buyer
        and seller arrange payment and postage directly between themselves. We do not handle money
        between you, hold funds in escrow, verify condition, or arrange shipping or customs. See our{' '}
        <Link to="/marketplace-terms" className="font-semibold underline underline-offset-2">
          marketplace terms
        </Link>
        .
      </Callout>

      {error ? <ErrorNotice message={error} /> : null}

      {mine?.listings.length ? (
        <section>
          <h2 className="text-lg font-semibold text-ink">Your listings</h2>
          <ul className="mt-4 space-y-3">
            {mine.listings.map((l) => (
              <li key={l.id} className="card flex flex-wrap items-center gap-3 p-4">
                <div className="flex-1">
                  <p className="font-semibold text-ink">{l.title}</p>
                  <p className="text-sm text-muted">
                    {l.status} · {l.listingFeePaid ? 'listing fee paid' : 'listing fee unpaid'}
                  </p>
                </div>
                {!l.listingFeePaid ? (
                  <button type="button" className="btn-primary w-full sm:w-auto" onClick={() => void pay(l.id)}>
                    Pay listing fee to publish
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <NewListingForm onCreated={() => void qc.invalidateQueries({ queryKey: ['listings-mine'] })} />

      <section>
        <h2 className="text-lg font-semibold text-ink">Available now</h2>
        {!data?.listings.length ? (
          <div className="mt-4">
            <EmptyState title="No listings yet" body="Be the first to list a book." />
          </div>
        ) : (
          <ul className="mt-4 grid gap-4 sm:grid-cols-2">
            {data.listings.map((l) => (
              <li key={l.id} className="card p-4 sm:p-5">
                <h3 className="font-semibold text-ink">{l.title}</h3>
                <p className="mt-0.5 text-sm text-muted">
                  {[l.author, l.edition].filter(Boolean).join(' · ')}
                </p>
                <p className="mt-3 text-xl font-bold text-ink">
                  {(l.priceCents / 100).toFixed(2)} {l.currency}
                </p>
                <p className="mt-1 text-sm capitalize text-muted">
                  {l.condition.replace('_', ' ')}
                  {l.location ? ` · ${l.location}` : ''}
                </p>
                {l.description ? <p className="mt-3 text-sm text-muted">{l.description}</p> : null}
                {l.contactEmail ? (
                  <a href={`mailto:${l.contactEmail}`} className="btn-secondary mt-4 w-full">
                    Contact the seller
                  </a>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function NewListingForm({ onCreated }: { onCreated: () => void }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: '', author: '', edition: '', isbn: '', condition: 'good', price: '', location: '', description: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/marketplace/listings', {
        title: form.title,
        author: form.author || null,
        edition: form.edition || null,
        isbn: form.isbn || null,
        condition: form.condition,
        priceCents: Math.round(Number(form.price) * 100),
        location: form.location || null,
        description: form.description || null,
      });
      onCreated();
      setOpen(false);
      setForm({ title: '', author: '', edition: '', isbn: '', condition: 'good', price: '', location: '', description: '' });
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Could not create the listing.');
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button type="button" className="btn-primary w-full sm:w-auto" onClick={() => setOpen(true)}>
        List a book
      </button>
    );
  }

  return (
    <form onSubmit={submit} className="card space-y-4 p-4 sm:p-6">
      <h2 className="text-lg font-semibold text-ink">List a book</h2>
      {error ? <ErrorNotice message={error} /> : null}
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label className="label" htmlFor="l-title">Title</label>
          <input id="l-title" required className="input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        </div>
        <div>
          <label className="label" htmlFor="l-author">Author / publisher</label>
          <input id="l-author" className="input" value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} />
        </div>
        <div>
          <label className="label" htmlFor="l-edition">Edition</label>
          <input id="l-edition" className="input" value={form.edition} onChange={(e) => setForm({ ...form, edition: e.target.value })} />
        </div>
        <div>
          <label className="label" htmlFor="l-isbn">ISBN</label>
          <input id="l-isbn" className="input" value={form.isbn} onChange={(e) => setForm({ ...form, isbn: e.target.value })} />
        </div>
        <div>
          <label className="label" htmlFor="l-cond">Condition</label>
          <select id="l-cond" className="input" value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })}>
            {CONDITIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
        <div>
          <label className="label" htmlFor="l-price">Asking price (AUD)</label>
          <input id="l-price" type="number" min={0} step="0.01" required className="input" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
        </div>
        <div>
          <label className="label" htmlFor="l-loc">Location</label>
          <input id="l-loc" className="input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
        </div>
        <div className="sm:col-span-2">
          <label className="label" htmlFor="l-desc">Description</label>
          <textarea id="l-desc" rows={3} className="input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <button type="submit" className="btn-primary" disabled={busy}>
          {busy ? 'Saving…' : 'Create draft listing'}
        </button>
        <button type="button" className="btn-ghost" onClick={() => setOpen(false)}>Cancel</button>
      </div>
      <p className="text-xs text-muted">
        Your listing stays a draft until the one-off listing fee is paid.
      </p>
    </form>
  );
}
